import React, { useState } from 'react';
import {Button, Typography, Grid, Card, CardContent,} from '@mui/material';
import { MatrixDisplay, MatrixPairDisplay } from './MatrixPairDisplay';

const generateRandomMatrix = (n, modulus) => {
    let matrix = [];
    for (let i = 0; i < n; i++) {
        matrix[i] = [];
        for (let j = 0; j < n; j++) {
            matrix[i][j] = Math.floor(Math.random() * modulus);
        }
    }
    return matrix;
};

const addMatrices = (A, B, modulus) => A.map((row, i) => row.map((_, j) => (A[i][j] + B[i][j]) % modulus));

const AverageCaseMatrixMultDisplay = () => {
    const modulus = 11;
    const [A] = useState(generateRandomMatrix(2, modulus));
    const [B] = useState(generateRandomMatrix(2, modulus));
    const [X, setX] = useState(null);
    const [Y, setY] = useState(null);

    const runAlgorithm = () => {
        setX(generateRandomMatrix(2, modulus));
        setY(generateRandomMatrix(2, modulus));
    };

    return (
        <Card style={{width: '100%'}}>
            <CardContent>
                <Typography variant="h5" gutterBottom align="center">
                    Worst-Case to Average-Case Matrix Multiplication Reduction Visualization
                </Typography>

                <Typography variant="h6" align="center" style={{ margin: '10px 0' }}>
                    Modulus: {modulus}
                </Typography>
                <Typography variant={'h8'}>
                    	From Manuel Blum, Michael Luby, Ronitt Rubinfeld:
                    Self-Testing/Correcting with Applications to Numerical Problems.
                    STOC 1990
                </Typography>
                <Grid container spacing={1} justifyContent="center">
                    <MatrixDisplay matrix={A} title="Worst-Case Input: Matrix A" />
                    <MatrixDisplay matrix={B} title="Worst-Case Input: Matrix B" />
                </Grid>
                <Grid container justifyContent="center" style={{ marginTop: '20px' }}>
                    <Button variant="contained" onClick={runAlgorithm}>Run Algorithm</Button>
                </Grid>
                {X && Y && (
                    <>
                        <Grid container spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
                            <MatrixDisplay matrix={X} title="Randomly Sampled: Matrix X" />
                            <MatrixDisplay matrix={Y} title="Randomly Sampled: Matrix Y" />
                        </Grid>
                        <Typography variant="h6" align="center" style={{ margin: '20px 0' }}>
                            A x B = (A+X) x (B+Y) - (A+X) x Y - X x (B+Y) + X x Y
                        </Typography>
                        <Grid item container direction="column" alignItems="center" spacing={1} justifyContent="center">
                            <MatrixPairDisplay
                                M={addMatrices(A, X, modulus)}
                                N={addMatrices(B, Y, modulus)}
                                textM="(A+X)"
                                textN="(B+Y)"
                                textMTimesN="(A+X) x (B+Y)"
                                modulus={modulus} />
                            <MatrixPairDisplay
                                M={addMatrices(A, X, modulus).map(row => row.map(val => -val  % modulus))}
                                N={Y}
                                textM="-(A+X)"
                                textN="Y"
                                textMTimesN="-(A+X) x Y"
                                modulus={modulus} />
                            <MatrixPairDisplay
                                M={X.map(row => row.map(val => -val  % modulus))}
                                N={addMatrices(B, Y, modulus)}
                                textM="-X"
                                textN="(B+Y)"
                                textMTimesN="-X x (B+Y)"
                                modulus={modulus} />
                            <MatrixPairDisplay
                                M={X}
                                N={Y}
                                textM="X"
                                textN="Y"
                                textMTimesN="X x Y"
                                modulus={modulus} />

                            <hr style={{ width: '100%', borderTop: '1px solid black', margin: '20px 0' }} />
                            <Grid item container direction="column" alignItems="center" spacing={1} justifyContent="center">
                                <MatrixPairDisplay
                                    M={A}
                                    N={B}
                                    textM="Matrix A"
                                    textN="Matrix B"
                                    textMTimesN="A x B"
                                    modulus={modulus} />
                            </Grid>
                        </Grid>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

const ToggleVisualization = () => {
    const [showVisualization, setShowVisualization] = useState(false);

    const handleToggle = () => {
        setShowVisualization(!showVisualization);
    };

    return (
        <Card style={{backgroundColor: 'rgba(206,225,209,0.9)', width: '100%'}}>
            <CardContent >
                <Button
                    variant="contained"
                    onClick={handleToggle}
                    style={{ backgroundColor: '#230736', color: 'white' }} // Dark purple background and white text
                >
                    {showVisualization ? 'Hide Visualization' : 'Show Average-Case Matrix Multiplication Visualization'}
                </Button>
                {showVisualization && <AverageCaseMatrixMultDisplay />}
            </CardContent>
        </Card>
    );
};

export default ToggleVisualization;
